import axios, { AxiosResponse } from "axios";
import { CreateVotingParams } from "../types/createVoting";
import { VotingQuestionArrayType } from "../types/votingQuestions";
import {
  ApiVotingListType,
  GetPublicVotingsParams,
  GetOrganizerVotingsParams,
  ApiVotingDraftDetails,
  ParticipationItem
} from "../types/votingsList";
import { axiosClient } from "./axiosClient";
import { ApiVotingDetails, PostVoteParams } from "../types/votingView";
import { VotingResults, VotingParticipants, VotingVerification } from "../types/votingResults";

export const getVotingResults = (id: string) => axiosClient.get<VotingResults>(`votings/${id}/results`);

// Publiczny werdykt weryfikacji zbiorczego odcisku (liczy teraz vs odczyt z łańcucha).
// skipServerErrorRedirect: to wywołanie tła nie może wywalić całej strony na /blad-serwera — karta po prostu się ukryje.
export const getVotingVerification = (id: string) =>
  axiosClient.get<VotingVerification>(`votings/${id}/verification`, {
    skipServerErrorRedirect: true
  } as never);

export const getVotingVoters = (id: string) => axiosClient.get<VotingParticipants>(`votings/${id}/voters`);

// Jawne głosy zalogowanego wyborcy zakotwiczone on-chain (per pytanie) + linki do eksploratora.
// skipServerErrorRedirect: tło — brak danych/uprawnień nie może wywalić strony.
export type OpenVoteItem = { question: string | null; answer: string | null; txid: string; explorer_url: string | null };
export const getMyOpenVotes = (id: string) =>
  axiosClient.get<{ open_votes: OpenVoteItem[] }>(`votings/${id}/my_open_votes`, {
    skipServerErrorRedirect: true
  } as never);

// Stan zakotwiczenia głosowania on-chain (publiczne): create_voting per pytanie + anchor wyniku.
export type BlockchainQuestionAnchor = {
  id: number;
  question: string | null;
  onchain_voting_id: string | null;
  explorer_url: string | null;
};
export type BlockchainInfoData = {
  secret: boolean;
  anchored: boolean;
  finished: boolean;
  explorer_base: string | null;
  questions: BlockchainQuestionAnchor[];
  result: {
    txid: string | null;
    anchored_at: string | null;
    explorer_url: string | null;
    verification_url: string | null;
    protocol_url: string | null;
  };
};
export const getBlockchainInfo = (id: string) =>
  axiosClient.get<BlockchainInfoData>(`votings/${id}/blockchain_info`, {
    skipServerErrorRedirect: true
  } as never);

export const addVotingVoters = (id: string, emails: string[]) =>
  axiosClient.post<{ added: number; total: number }>(`votings/${id}/voters`, { emails });

export const getParticipations = () =>
  axiosClient.get<{ votings: ParticipationItem[] }>("user/votings/participations");

const appendEmails = (emails: { id: number; value: string }[] = [], formData: FormData) => {
  emails.forEach(email => {
    formData.append("voting[voters_attributes][][email]", email.value);
  });
};

const appendQuestions = (questions: VotingQuestionArrayType[], formData: FormData) => {
  questions.forEach(question => {
    formData.append("voting[questions_attributes][][content]", question.transformedQuestion ?? question.question);
    formData.append("voting[questions_attributes][][qtype]", question.qtype ?? "single");
    formData.append("voting[questions_attributes][][allow_abstain]", String(!!question.allowAbstain));
    formData.append("voting[questions_attributes][][threshold_type]", question.thresholdType ?? "plurality");
    if (question.thresholdType === "custom" && question.thresholdPercent != null) {
      formData.append("voting[questions_attributes][][threshold_percent]", String(question.thresholdPercent));
    }
    if (question.qtype === "multiple" && question.maxSelections) {
      formData.append("voting[questions_attributes][][max_selections]", String(question.maxSelections));
    }
    question.answers.forEach(answer => {
      formData.append("voting[questions_attributes][][answers_attributes][][content]", answer.content);
    });
  });
};

const getFormData = (data: Partial<CreateVotingParams>): FormData => {
  const formData = new FormData();

  const { emails, questions, group_ids, ...restParams } = data;
  const nonArrayData = { ...restParams, status: "planned" };

  Object.entries(nonArrayData).forEach(([key, value]) => {
    value && formData.append(`voting[${key}]`, value);
  });

  group_ids?.forEach(id => formData.append("voting[group_ids][]", String(id)));

  emails && appendEmails(emails, formData);
  questions && appendQuestions(questions, formData);

  return formData;
};

export const createVoting = async (params: Partial<CreateVotingParams>) => {
  try {
    return await axiosClient.post("votings", getFormData(params), {
      headers: { "Content-Type": "multipart/form-data" }
    });
  } catch (err) {
    console.error(err);
  }
};

export const getPublicVotings = async (params: GetPublicVotingsParams) =>
  axiosClient.get<ApiVotingListType>("votings", { params });

export const getCsvTemplate = async (): Promise<AxiosResponse<Blob> | undefined> => {
  try {
    return axios.get<Blob>(`${process.env.REACT_APP_BACKEND_URL}/voters_template.csv`, {
      responseType: "blob"
    });
  } catch (err) {
    console.error(err);
  }
};

export const getVotingDetails = async (id: string, email?: string) => {
  try {
    return await axiosClient.get<ApiVotingDetails | ApiVotingDraftDetails>(`votings/${id}`, {
      params: { voter_email: email }
    });
  } catch (err: any) {
    console.error(err);

    return err?.response;
  }
};

export const sendVote = async (id: string, vote: PostVoteParams) => {
  try {
    return await axiosClient.post(`votings/${id}/vote`, { vote });
  } catch (err: any) {
    console.error(err);

    return err?.response;
  }
};
export const confirmVote = async (token: string) =>
  axiosClient.post("vote/confirm", {
    confirmation_token: token
  });

export const getOrganizerVotings = async (params: GetOrganizerVotingsParams) =>
  axiosClient.get<ApiVotingListType>("user/votings", { params });

export const getVotingOrganizer = async (id: string) => {
  try {
    return await axiosClient.get<ApiVotingDetails>(`votings/${id}/user`);
  } catch (err: any) {
    console.error(err);

    return err?.response;
  }
};

export const sendVotingProtocole = async (id: string) => axiosClient.post(`user/votings/${id}/send_protocol`);

export const deleteVotingDraft = async (id: string) => axiosClient.delete(`votings/${id}`);

// Zgłoszenie tylko dla zalogowanych — e-mail bierze backend z konta, bez recaptcha.
export const sendReport = async (id: string, reason: string, reportableType: string) => {
  try {
    return await axiosClient.post(`report`, { id, reason, type: reportableType });
  } catch (err: any) {
    console.error(err);

    return err?.response;
  }
}